import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { CreatePasswordStep } from "@circle-react/components/OnboardingPage/OtpConfirmationPage/CreatePasswordStep";
import { OtpSignInPasswordForm } from "@circle-react/components/OnboardingPage/OtpSignInPage/OtpSignInPasswordForm";
import { OtpChallengeContent } from "@circle-react/components/OtpChallenge/OtpChallengeContent";
import { OtpChallengeModal } from "@circle-react/components/OtpChallenge/OtpChallengeModal";
import { OtpChallengeModalCompass } from "@circle-react/components/OtpChallenge/OtpChallengeModalCompass";
import { signOutPath } from "@circle-react/helpers/urlHelpers";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useReducer,
} from "react";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

interface OtpChallengeState {
  error: boolean | null;
  failedAttempts: number;
  isLoading: boolean;
  isOpen: boolean;
}

type RetryWithCode<T> = (code: string) => Promise<T>;

const otpRetryResultRequiresPasswordSetup = (result: unknown): boolean =>
  Boolean(
    result &&
    typeof result === "object" &&
    "requires_password_setup" in result &&
    (result as { requires_password_setup?: boolean }).requires_password_setup,
  );

export type Variant = "modal" | "modal-fullscreen" | "embedded";
export type OtpChallengeFlow = "sign_in" | "sign_up";

const OTP_STEP = {
  OTP: "otp",
  PASSWORD: "password",
  PASSWORD_SETUP: "password_setup",
} as const;

type OtpStep = (typeof OTP_STEP)[keyof typeof OTP_STEP];

interface OtpChallengeContextType {
  close: () => void;
  runMutationWithOtp: <T>(retryWithOtp: RetryWithCode<T>) => Promise<T>;
  setEmail: (email?: string) => void;
  setFlow: (flow?: OtpChallengeFlow) => void;
  setShowSignInWithPassword: (showSignInWithPassword: boolean) => void;
  setVariant: (variant: Variant) => void;
  showPasswordStep: () => void;
}

const OtpChallengeContext = createContext<OtpChallengeContextType | null>(null);
OtpChallengeContext.displayName = "OtpChallengeContext";

export const useOtpChallengeContext = () => {
  const ctx = useContext(OtpChallengeContext);
  if (!ctx) throw new Error("OtpChallengeProvider is missing");
  return ctx;
};

type OtpPromise = {
  retryWithOtp: (code: string) => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
} | null;

interface OtpChallengeProviderProps {
  children: React.ReactNode;
}

const simpleMergeReducer = (
  state: OtpChallengeState,
  action: Partial<OtpChallengeState>,
): OtpChallengeState => ({
  ...state,
  ...action,
});

const MAX_OTP_ATTEMPTS = 3;

const initialState: OtpChallengeState = {
  isOpen: false,
  isLoading: false,
  error: null,
  failedAttempts: 0,
};

export const OtpChallengeProvider = ({
  children,
}: OtpChallengeProviderProps) => {
  const toast = useToast();
  const { isCircleV4 } = useCircleV4();
  const { currentCommunity } = useCurrentCommunityContext();

  const [state, dispatch] = useReducer(simpleMergeReducer, initialState);

  const mutationWithOtpRef = useRef<OtpPromise>(null);

  const [email, setEmail] = useState<string>();
  const [flow, setFlow] = useState<OtpChallengeFlow>();
  const [variant, setVariant] = useState<Variant>("modal");
  const [canShowSignInWithPassword, setShowSignInWithPassword] =
    useState(false);
  const [step, setStep] = useState<OtpStep>(OTP_STEP.OTP);
  const [
    wasPasswordStepEnteredWithoutOtp,
    setWasPasswordStepEnteredWithoutOtp,
  ] = useState(false);

  const shouldShowEmbeddedOtpChallenge = variant === "embedded" && state.isOpen;
  const shouldShowModalOtpChallenge = variant !== "embedded";

  const close = useCallback(() => {
    dispatch(initialState);
    mutationWithOtpRef.current = null;
    setStep(OTP_STEP.OTP);
    setWasPasswordStepEnteredWithoutOtp(false);
  }, []);

  const showPasswordStep = useCallback(() => {
    setStep(OTP_STEP.PASSWORD);
    setWasPasswordStepEnteredWithoutOtp(true);
    dispatch({ isOpen: true });
  }, []);

  const abandonMandatoryPasswordSetup = useCallback(() => {
    window.location.href = signOutPath();
  }, []);

  const handleCancel = useCallback(() => {
    const mutationWithOtp = mutationWithOtpRef.current;

    if (mutationWithOtp) {
      mutationWithOtp.reject(
        new Error(t("otp_challenge_modal.cancelled"), {
          cause: { otpChallengeCancelled: true },
        }),
      );
    }

    close();
  }, [close]);

  const runMutationWithOtp = useCallback(
    <T,>(retryWithOtp: RetryWithCode<T>) =>
      new Promise<T>((resolve, reject) => {
        mutationWithOtpRef.current = {
          retryWithOtp: (code: string) =>
            retryWithOtp(code) as Promise<unknown>,
          resolve: (value: unknown) => resolve(value as T),
          reject,
        };

        // A real OTP challenge always means "show the code entry step," even
        // when the caller (e.g. OtpSignInPasswordForm) is itself rendering
        // because step was already OTP_STEP.PASSWORD — otherwise the password
        // form keeps rendering over a pending OTP challenge it can't satisfy.
        setStep(OTP_STEP.OTP);
        dispatch({ isOpen: true });
      }),
    [],
  );

  const handleSubmit = useCallback(
    async (code: string) => {
      const mutationWithOtp = mutationWithOtpRef.current;

      if (!mutationWithOtp) return;

      dispatch({ isLoading: true, error: null });

      try {
        const result = await mutationWithOtp.retryWithOtp(code.trim());
        if (otpRetryResultRequiresPasswordSetup(result)) {
          dispatch({ isLoading: false });
          setStep(OTP_STEP.PASSWORD_SETUP);
          return;
        }
        mutationWithOtp.resolve(result);
      } catch (error: any) {
        if (error.cause?.errorDetails?.otp_challenge_validation_failed) {
          const failedAttempts = state.failedAttempts + 1;

          dispatch({
            isLoading: false,
            error: true,
            failedAttempts,
          });

          toast.error(String(error.message));

          if (failedAttempts >= MAX_OTP_ATTEMPTS) {
            handleCancel();
          }
        } else {
          mutationWithOtp.reject(error);
          close();
        }
      }
    },
    [close, state.failedAttempts, toast],
  );

  const value = useMemo(
    () => ({
      runMutationWithOtp,
      setEmail,
      setFlow,
      setVariant,
      setShowSignInWithPassword,
      close,
      showPasswordStep,
    }),
    [
      runMutationWithOtp,
      setEmail,
      setFlow,
      setVariant,
      setShowSignInWithPassword,
      close,
      showPasswordStep,
    ],
  );

  return (
    <OtpChallengeContext.Provider value={value}>
      {!shouldShowEmbeddedOtpChallenge && children}
      {shouldShowEmbeddedOtpChallenge &&
        (step === OTP_STEP.PASSWORD_SETUP && email ? (
          <CreatePasswordStep
            email={email}
            onCancel={abandonMandatoryPasswordSetup}
            community={currentCommunity}
            className="min-h-screen bg-primary px-4 sm:px-8"
          />
        ) : step === OTP_STEP.PASSWORD && email ? (
          <OtpSignInPasswordForm
            email={email}
            onShowOtpForm={() => setStep(OTP_STEP.OTP)}
            onCancel={handleCancel}
            className="min-h-screen bg-primary px-4 sm:px-8"
            showSignInWithCode={!wasPasswordStepEnteredWithoutOtp}
          />
        ) : (
          <OtpChallengeContent
            email={email}
            flow={flow}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={state.isLoading}
            error={state.error}
            className="min-h-screen bg-primary px-4 sm:px-8"
            showSignInWithPassword={canShowSignInWithPassword}
            onSignInWithPassword={() => setStep(OTP_STEP.PASSWORD)}
            useEntryFlowBranding
          />
        ))}
      {shouldShowModalOtpChallenge &&
        (isCircleV4 ? (
          <OtpChallengeModalCompass
            isOpen={state.isOpen}
            variant={variant}
            email={email}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={state.isLoading}
            error={state.error}
            showSignInWithPassword={canShowSignInWithPassword}
            onSignInWithPassword={() => setStep(OTP_STEP.PASSWORD)}
            isPasswordStep={step === OTP_STEP.PASSWORD}
            onShowOtpForm={() => setStep(OTP_STEP.OTP)}
            showSignInWithCode={!wasPasswordStepEnteredWithoutOtp}
          />
        ) : (
          <OtpChallengeModal
            isOpen={state.isOpen}
            variant={variant}
            email={email}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={state.isLoading}
            error={state.error}
            showSignInWithPassword={canShowSignInWithPassword}
            onSignInWithPassword={() => setStep(OTP_STEP.PASSWORD)}
            isPasswordStep={step === OTP_STEP.PASSWORD}
            onShowOtpForm={() => setStep(OTP_STEP.OTP)}
            showSignInWithCode={!wasPasswordStepEnteredWithoutOtp}
          />
        ))}
    </OtpChallengeContext.Provider>
  );
};
